import { useEffect, useState } from "react"
import { Button, Col, FloatingLabel, Form, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap"
import { useGameContext } from "../../../Commons/GameContext"
import { FeedbackButton, IMG, getDeckFormatsArray, shuffleArray } from "../../../Commons/Functions"
import { useNavigate } from "react-router"

export default function DeckPickerModal(props) {
    const { gameData } = useGameContext()
    const [selectedDeck, setSelectedDeck] = useState(false)
    const [availableDecks, setAvailableDecks] = useState([])
    const [lastSelectedTab, setLastSelectedTab] = useState("yours")
    const [availablePreconstructedDecks, setAvailablePreconstructedDecks] = useState(false)
    const [showMulliganSection, setShowMulliganSection] = useState(false)
    const [isDeckFormatValid, setIsDeckFormatValid] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        const currentGame = props.currentGame
        if (!currentGame || !currentGame.selected) { return }

        const key = "user_decks_" + currentGame.selected
        let decksSavedData = localStorage.getItem(key);

        let newAvailableDecks = []

        if (decksSavedData) {
            const decksSaved = JSON.parse(decksSavedData);

            decksSaved.forEach(deckId => {
                const newDeckData = localStorage.getItem(key + '_' + deckId);
                let parsedDeckData = JSON.parse(newDeckData)
                const parsedDeckFormats = getDeckFormatsArray(parsedDeckData.format)
                if (parsedDeckFormats.includes(props.gameOptions.format.title)) {
                    newAvailableDecks.push(parsedDeckData)
                }
            })
            if (newAvailableDecks.length > 0 && !selectedDeck) {
                setSelectedDeck(newAvailableDecks[0])
            }
        } else {
            console.log("ERROR: No deck selected");
        }

        // Update precon list
        if (availablePreconstructedDecks) {
            let newPreconList = []
            availablePreconstructedDecks.forEach(d => {
                const preconFormats = getDeckFormatsArray(d.format)
                if (preconFormats.includes(props.gameOptions.format.title)) {
                    newPreconList.push(d)
                }
            })
            availablePreconstructedDecks.forEach(d => {
                const preconFormats = getDeckFormatsArray(d.format)
                if (!preconFormats.includes(props.gameOptions.format.title)) {
                    newPreconList.push(d)
                }
            })
            setAvailablePreconstructedDecks(newPreconList)
        }

        setAvailableDecks(newAvailableDecks)
    }, [props.gameOptions])

    useEffect(() => {
        const formats = getDeckFormatsArray(selectedDeck.format)
        if (formats.includes(props.gameOptions.format.title)) {
            setIsDeckFormatValid(true)
        } else {
            setIsDeckFormatValid(false)
        }
    }, [selectedDeck])

    if (showMulliganSection) {
        return (<MulliganSection
            currentGame={props.currentGame}
            gameData={props.gameData}
            createGameCardFromCardData={props.createGameCardFromCardData}
            sendToHistory={props.sendToHistory}
            selectedDeck={selectedDeck}
            show={props.show}
            handleClose={props.handleClose}
            start={props.start}
            gameOptions={props.gameOptions}
            onExited={() => {
                setShowMulliganSection(false)
                props.started()
            }}
        />)
    }

    async function loadPreconsFromUrl(url) {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Network response was not ok");

            const data = await response.json();

            if (Array.isArray(data)) {
                const decksForThisFormat = []
                data.forEach(d => {
                    const deckFormats = getDeckFormatsArray(d.format)
                    if (deckFormats.includes(props.gameOptions.format.title)) {
                        decksForThisFormat.push(d)
                    }
                })
                if (decksForThisFormat.length > 0) {
                    setSelectedDeck(decksForThisFormat[0])
                }
                // Adding precons not for this format at the end
                data.forEach(d => {
                    const deckFormats = getDeckFormatsArray(d.format)
                    if (!deckFormats.includes(props.gameOptions.format.title)) {
                        decksForThisFormat.push(d)
                    }
                })
                setAvailablePreconstructedDecks(decksForThisFormat);
            } else {
                console.warn("Expected an array but got:", data);
            }
        } catch (error) {
            console.error("Error loading JSON:", error);
        }
    }

    return (
        <Modal className="" backdrop="static" show={props.show} onHide={props.handleClose} centered>
            <Modal.Header>
                <Modal.Title>Deck selection</Modal.Title>
            </Modal.Header>
            {props.gameOptions && (<Modal.Body className='d-flex justify-content-between'>
                <p><b>Format:</b> {props.gameOptions.format.title}</p>
                <p><b>Starting player:</b> {props.gameOptions.startingPlayer.name}</p>
            </Modal.Body>)}

            <Tabs
                defaultActiveKey={lastSelectedTab}
                id="uncontrolled-tab-example"
                className="box-container"
                justify
            >
                <Tab eventKey="yours" title="Your decks"
                    onEntering={() => {
                        setLastSelectedTab("yours")
                        if (selectedDeck && availableDecks.length > 0) {
                            let index = availableDecks.findIndex((a) => a.id === selectedDeck.id)
                            if (index === -1) {
                                setSelectedDeck(availableDecks[0])
                            }
                        }
                    }}
                >
                    <div className="box-container flex-column">
                        <p className="mb-3">Your decks for this format.</p>
                        {availableDecks.length == 0 && (
                            <Modal.Body>
                                <p>You don't have any decks for this format, use preconstructed decks.</p>
                            </Modal.Body>
                        )}
                        {
                            availableDecks.length > 0 && (
                                <FloatingLabel className="" controlId={"format-selection"} label={"Choose your deck"}>
                                    <Form.Select aria-label="Format select"
                                        defaultValue={selectedDeck.id}
                                        onChange={(e) => {
                                            let index = availableDecks.findIndex((a) => a.id === e.currentTarget.value)
                                            if (index !== -1) {
                                                setSelectedDeck(availableDecks[index])
                                            }
                                        }}
                                    >
                                        {availableDecks.map((v, j) => {
                                            return (<option key={v.id} value={v.id}>{v.title} </option>)
                                        })}
                                    </Form.Select>
                                </FloatingLabel>
                            )
                        }
                    </div>
                </Tab>
                <Tab eventKey="precons" title="Preconstructed decks"
                    onEntering={() => {
                        if (availablePreconstructedDecks === false) {
                            if (gameData.defaultRessources.decksUrl && gameData.defaultRessources.decksUrl.length > 0) {
                                loadPreconsFromUrl(gameData.defaultRessources.decksUrl)
                            } else {
                                setAvailablePreconstructedDecks([])
                            }
                        } else if (selectedDeck && availablePreconstructedDecks.length > 0) {
                            let index = availablePreconstructedDecks.findIndex((a) => a.id === selectedDeck.id)
                            if (index === -1) {
                                setSelectedDeck(availablePreconstructedDecks[0])
                            }
                        }
                        setLastSelectedTab("precons")
                    }}
                >
                    <div className="box-container flex-column">
                        <p className="mb-3">You can play with any preconstructed decks this game offers for this format.</p>

                        {!availablePreconstructedDecks && (<div className="w-100 my-5 d-flex justify-content-center">
                            <Spinner animation="border" role="status"></Spinner>
                        </div>)}
                        {availablePreconstructedDecks.length === 0 && (
                            <p>This game has no preconstructed decks.</p>
                        )}
                        {props.show && availablePreconstructedDecks.length > 0 && (
                            <FloatingLabel className="" controlId={"format-selection"} label={"Choose your precon"}>
                                <Form.Select aria-label="Precon select"
                                    defaultValue={selectedDeck.id}
                                    onChange={(e) => {
                                        let index = availablePreconstructedDecks.findIndex((a) => a.id === e.currentTarget.value)
                                        if (index !== -1) {
                                            setSelectedDeck(availablePreconstructedDecks[index])
                                        }
                                    }}
                                >
                                    {availablePreconstructedDecks.map((v, j) => {
                                        const formats = getDeckFormatsArray(v.format)
                                        // A AMELIORER NIVEAU PERF ET ENLEVER PROPS.SHOW
                                        console.log(props.show)
                                        if (formats.includes(props.gameOptions.format.title)) {
                                            return (<option key={v.id} value={v.id}>{v.title} </option>)
                                        } else {
                                            return (<option disabled key={v.id} value={v.id}>{v.title}</option>)
                                        }
                                    })}
                                </Form.Select>
                            </FloatingLabel>
                        )}
                    </div>
                </Tab>
            </Tabs>
            <Modal.Footer>
                {availableDecks.length == 0 && (
                    <Button variant="danger" className="me-auto" onClick={() => {
                        navigate("/")
                        //props.handleClose()
                    }}>
                        Back to main menu
                    </Button>
                )}
                {selectedDeck && (<Button variant="primary" onClick={() => {
                    setShowMulliganSection(true)
                    //props.handleClose()
                }}
                    disabled={!isDeckFormatValid}
                >
                    Continue
                </Button>)}
            </Modal.Footer>
        </Modal >
    )
}

function SideboardSection(props) {
    const [hoveredCard, setHoveredCard] = useState(false)
    const [decks, setDecks] = useState({
        sideboard: [...props.sideboard],
        deck: [...props.deck]
    })

    useEffect(() => {
        let tmp = { ...decks }
        tmp.deck.sort((a, b) => a.cardData.name.localeCompare(b.cardData.name))
        tmp.sideboard.sort((a, b) => a.cardData.name.localeCompare(b.cardData.name))
        setDecks(tmp)
    }, [])

    function swap(index, source, destination) {
        let tmp = { ...decks }

        let card = tmp[source].splice(index, 1)[0]

        tmp[destination].push(card)
        tmp[destination].sort((a, b) => a.cardData.name.localeCompare(b.cardData.name))

        setDecks(tmp)
    }

    return (<Modal className="sideboard-section fullscreen-modal" backdrop="static" show={props.show} onHide={props.handleClose} centered >
        <Modal.Header>
            <Modal.Title>Swap cards with your sideboard</Modal.Title>
            <Button className="ms-auto" variant="primary" onClick={() => {
                props.handleClose(decks.deck, decks.sideboard)
            }}>
                Continue
            </Button>
        </Modal.Header>
        <Modal.Body>
            <Row className=''>
                <Col xs={4}>
                    {hoveredCard && (<div className="sticky-top">
                        <IMG className="w-100 h-100 mt-3 hovered-card" src={hoveredCard.cardData.face.front.image} />
                    </div>)}
                </Col>
                <Col xs={4}>
                    <h5>Deck ({decks.deck.length})</h5>
                    {decks.deck.map((c, i) => {
                        return (<Sideboard_Card key={c.id} index={i} card={c} swap={() => swap(i, "deck", "sideboard")} setHoveredCard={setHoveredCard} />)
                    })}
                </Col>
                <Col xs={4}>
                    <h5>Sideboard ({decks.sideboard.length})</h5>
                    {decks.sideboard.map((c, i) => {
                        return (<Sideboard_Card key={c.id} index={i} card={c} swap={() => swap(i, "sideboard", "deck")} setHoveredCard={setHoveredCard} />)
                    })}
                </Col>
            </Row>
        </Modal.Body>
    </Modal>)
}

function Sideboard_Card({ index, card, swap, setHoveredCard }) {
    return (
        <div className="sideboard-card d-flex w-100 justify-content-between"
            onPointerDown={swap}
            onPointerEnter={() => setHoveredCard(card)}
        >
            <div className="d-flex w-100 overflow-hidden" style={{ maxWidth: "calc(100% - 7rem)" }}>
                <p className="my-auto mx-2 text-center">{index + 1}</p>
                <p className="my-auto text-nowrap"
                >{card.cardData.name}</p>
            </div>
            <p className="text-nowrap me-2 my-auto text-end" style={{ width: "6rem" }}><i>Click to swap</i></p>
        </div>
    )
}

function MulliganSection(props) {
    const { gameCards } = useGameContext()
    const [deck, setDeck] = useState([])
    const [sideboard, setSideboard] = useState([])
    const [hand, setHand] = useState([])
    const [mulliganCount, setMulliganCount] = useState(0)
    const [cardsAlreadyOnBoard, setCardsAlreadyOnBoard] = useState([])
    const [selectedCardIndexes, setSelectedCardIndexes] = useState([])
    const mulliganInfo = props.gameOptions.format.mulligan ? props.gameOptions.format.mulligan : {
        startingHandSize: 7,
        info: "ERROR Loading mulligan info"
    }
    const handSize = mulliganInfo.startingHandSize

    const [showSideboardSection, setShowSideboardSection] = useState(false)

    useEffect(() => {
        const deckData = props.selectedDeck
        if (deckData) {
            const sectionsFile = props.gameOptions.format.sections

            deckData.deckList.categoriesOrder.forEach(cat => {
                let newCat = []
                deckData.deckList[cat].forEach((card, i) => {
                    if (gameCards[card.id]) {
                        let cardData = { ...gameCards[card.id] }
                        cardData.count = card.count
                        newCat.push(cardData)
                    }
                })
                deckData.deckList[cat] = newCat
            })

            let newSideboard = []
            if (deckData.deckList["Sideboard"]) {
                deckData.deckList["Sideboard"].forEach((card, i) => {
                    if (gameCards[card.id]) {
                        let cardData = { ...gameCards[card.id] }
                        cardData.count = 1
                        for (let n = 0; n < card.count; n++) {
                            newSideboard.push(props.createGameCardFromCardData(cardData))
                        }
                    }
                })
            }

            let newDeck = []
            let newCardsOnBoard = []

            deckData.deckList.categoriesOrder.forEach((cat) => {
                let startOnBoard = false
                let cardSection = "" + cat
                if (sectionsFile.categoriesAlreadyOnBoard) {
                    sectionsFile.categoriesAlreadyOnBoard.forEach((c) => {
                        let source = "" + c
                        if (source.includes("->")) {
                            cardSection = source.split("->")[1]
                            source = source.split("->")[0]
                        }
                        if (cat === source) {
                            startOnBoard = true
                        }
                    })
                }
                deckData.deckList[cat].forEach((c) => {
                    for (let i = 0; i < c.count; i++) {
                        const newCard = props.createGameCardFromCardData(c)
                        if (startOnBoard) {
                            newCardsOnBoard.push({ card: newCard, section: cardSection })
                        } else {
                            newDeck.push(newCard)
                        }
                    }
                })
            })

            shuffleArray(newDeck)
            if (newSideboard.length == 0) {
                let newCards = newDeck.splice(-handSize)
                setHand(newCards)
            }
            setDeck(newDeck)
            setSideboard(newSideboard)
            setCardsAlreadyOnBoard(newCardsOnBoard)
            setShowSideboardSection(newSideboard.length !== 0)   // Only if deck has a sideboard
            setMulliganCount(0)
        }
    }, [])

    if (showSideboardSection) {
        return (<SideboardSection
            show={showSideboardSection}
            handleClose={(newDeck, newSideboard) => {
                let tmpDeck = [...newDeck]
                shuffleArray(tmpDeck)
                const newCards = tmpDeck.splice(-handSize)
                setHand(newCards)
                setDeck(tmpDeck)
                setShowSideboardSection(false)
                setSideboard(newSideboard)
            }}
            deck={deck}
            sideboard={sideboard}
        />)
    }

    function toggleSelectCard(index) {
        // Dont do anything if there are no action for selected cards
        if (!mulliganInfo.drawNewSelectedCards && !mulliganInfo.putSelectionAtBottom) {
            return
        }

        let tmp = [...selectedCardIndexes]
        let indexInTheIndexesFromTheArrayFullOfIndexes = tmp.findIndex(a => a == index)
        if (indexInTheIndexesFromTheArrayFullOfIndexes != -1) {
            tmp.splice(indexInTheIndexesFromTheArrayFullOfIndexes, 1)
        } else {
            tmp.push(index)
        }
        setSelectedCardIndexes(tmp)
    }

    return (<Modal className="fullscreen-modal" backdrop="static" show={props.show} onHide={props.handleClose} centered onExited={props.onExited}>
        <Modal.Header>
            <Modal.Title>Mulligan {mulliganCount > 0 ? `- ${mulliganCount} new hand${mulliganCount > 1 ? "s" : ""}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="box-container brighter">
                <p>{mulliganInfo.info}</p>
            </div>
            <div className='d-flex flex-column mulligan-sections w-100 position-relative mt-4'>
                <div className='d-flex pe-none mulligan-section w-100 position-absolute start-0 top-0 m-0'>
                    {hand.map((c, i) => {
                        const selected = selectedCardIndexes.includes(i)
                        return (<div key={i + "_" + c.id}
                            className={"h-100 wrapper mx-auto d-flex justify-content-center" + (selected ? " selected" : "") + (i % 2 === 0 ? " even" : " odd")}
                        >
                            <div onClick={() => {
                                toggleSelectCard(i)
                            }}>
                                <div className="img-wrapper w-100 h-100">
                                    <IMG
                                        className="h-100 w-auto"
                                        src={c.cardData.face.front.image}
                                    />
                                </div>
                            </div>
                        </div>)
                    })}
                </div>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <div className="d-flex me-auto">
                {selectedCardIndexes.length > 0 && mulliganInfo.drawNewSelectedCards && (<Button variant="secondary" onClick={() => {
                    props.sendToHistory("drew again " + selectedCardIndexes.length + " cards")
                    const indexesToRemove = [...selectedCardIndexes]
                    indexesToRemove.sort((a, b) => b - a);

                    let newHand = [...hand]
                    let removed = []
                    indexesToRemove.forEach(index => {
                        removed.push(newHand.splice(index, 1)[0]);
                    });
                    let newDeck = [...deck]
                    let newCards = newDeck.splice(-selectedCardIndexes.length)
                    setHand([...newHand, ...newCards])

                    newDeck = [...newDeck, ...removed]
                    shuffleArray(newDeck)
                    setDeck(newDeck)
                    setSelectedCardIndexes([])
                    setMulliganCount(mulliganCount + 1)
                }}>
                    Draw those cards again ({selectedCardIndexes.length})
                </Button>)}
                {mulliganInfo.drawNewHand && (<Button variant="secondary" onClick={() => {
                    props.sendToHistory("drew a new hand")
                    let newDeck = [...deck, ...hand]
                    shuffleArray(newDeck)
                    let newCards = newDeck.splice(-handSize)
                    setHand(newCards)
                    setDeck(newDeck)
                    setSelectedCardIndexes([])
                    setMulliganCount(mulliganCount + 1)
                }}>
                    Draw new hand
                </Button>)}
            </div>
            {selectedCardIndexes.length > 0 && mulliganInfo.putSelectionAtBottom && (<Button onClick={() => {
                props.sendToHistory("put back " + selectedCardIndexes.length + " cards at the bottom of its deck")
                const indexesToRemove = [...selectedCardIndexes]
                indexesToRemove.sort((a, b) => b - a);

                let newHand = [...hand]
                let removed = []
                indexesToRemove.forEach(index => {
                    removed.push(newHand.splice(index, 1)[0]);
                });
                let newDeck = [...removed, ...deck, ...newHand]
                props.start(newDeck, handSize - indexesToRemove.length, cardsAlreadyOnBoard, sideboard)
                props.handleClose()
            }}>
                Put selected cards at the bottom and start ({selectedCardIndexes.length})
            </Button>)}
            <FeedbackButton variant="primary" feedBack="starting" onClick={() => {
                let newDeck = [...deck, ...hand]
                props.start(newDeck, handSize, cardsAlreadyOnBoard, sideboard)
                props.handleClose()
            }}>
                Keep hand and start
            </FeedbackButton>
        </Modal.Footer>
    </Modal>)
}